import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Theme provider.
 *
 * Wraps `FluentProvider` so the app gets one line of setup instead of the
 * matchMedia/localStorage/token plumbing every consumer would otherwise repeat.
 *
 * Defaults to whatever the operating system is set to and updates live when the
 * user flips their OS between light and dark; an explicit override is
 * remembered in localStorage.
 */
import { FluentProvider } from '@fluentui/react-components';
import { createContext, use, useCallback, useEffect, useMemo, useState, } from 'react';
import { pacxaDarkTheme, pacxaLightTheme } from './theme.js';
const STORAGE_KEY = 'pacxa.colorScheme';
const DARK_QUERY = '(prefers-color-scheme: dark)';
const ColorSchemeContext = createContext(null);
function readStoredPreference() {
    if (typeof window === 'undefined')
        return 'system';
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === 'light' || stored === 'dark' || stored === 'system')
            return stored;
    }
    catch {
        // Private browsing or a blocked storage partition — fall back to the OS.
    }
    return 'system';
}
function readSystemScheme() {
    if (typeof window === 'undefined' || !window.matchMedia)
        return 'light';
    return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}
export function AppThemeProvider({ children, forceColorScheme, lightTheme = pacxaLightTheme, darkTheme = pacxaDarkTheme, }) {
    const [preference, setPreferenceState] = useState(readStoredPreference);
    const [systemScheme, setSystemScheme] = useState(readSystemScheme);
    // Track the OS setting even while an override is active, so clearing the
    // override snaps straight to the right theme.
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia)
            return;
        const media = window.matchMedia(DARK_QUERY);
        const onChange = (event) => {
            setSystemScheme(event.matches ? 'dark' : 'light');
        };
        media.addEventListener('change', onChange);
        setSystemScheme(media.matches ? 'dark' : 'light');
        return () => media.removeEventListener('change', onChange);
    }, []);
    const setPreference = useCallback((next) => {
        setPreferenceState(next);
        try {
            window.localStorage.setItem(STORAGE_KEY, next);
        }
        catch {
            // Not being able to remember the choice is not worth failing over.
        }
    }, []);
    const colorScheme = forceColorScheme ?? (preference === 'system' ? systemScheme : preference);
    // Native controls (scrollbars, form widgets, the browser's own UI) need to be
    // told, or they stay light inside a dark app.
    useEffect(() => {
        if (typeof document === 'undefined')
            return;
        document.documentElement.style.colorScheme = colorScheme;
    }, [colorScheme]);
    const value = useMemo(() => ({ preference, colorScheme, setPreference }), [preference, colorScheme, setPreference]);
    return (_jsx(ColorSchemeContext, { value: value, children: _jsx(FluentProvider, { theme: colorScheme === 'dark' ? darkTheme : lightTheme, style: { minHeight: '100%', display: 'flex', flexDirection: 'column' }, children: children }) }));
}
/** Read or change the active colour scheme. Must be used inside AppThemeProvider. */
export function useColorScheme() {
    const ctx = use(ColorSchemeContext);
    if (!ctx)
        throw new Error('useColorScheme must be used inside AppThemeProvider');
    return ctx;
}
//# sourceMappingURL=provider.js.map